import { useRef, useState } from "react";
import { motion } from "framer-motion";
import { Leaf, Home, CalendarClock, ChevronsLeftRight, BadgeCheck, ReceiptText } from "lucide-react";
import { scrollToForm } from "@/App";

const RealResults = () => {
  const [pct, setPct] = useState(50);
  const trackRef = useRef(null);
  const dragging = useRef(false);

  const setFromClientX = (clientX) => {
    const rect = trackRef.current.getBoundingClientRect();
    setPct(Math.min(96, Math.max(4, ((clientX - rect.left) / rect.width) * 100)));
  };

  return (
    <section
      data-testid="real-results-section"
      className="bg-cream py-20 md:py-28"
    >
      <div className="max-w-6xl mx-auto px-4 md:px-8 grid lg:grid-cols-2 gap-12 items-center">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
          className="relative bg-white rounded-[20px] p-8 md:p-10"
          style={{ boxShadow: "0 20px 45px rgba(1,27,71,0.14)" }}
        >
          <Leaf
            aria-hidden="true"
            className="absolute top-6 right-6 w-16 h-16 text-glight/30"
          />
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-gdark">
            Real results
          </p>
          <h2 className="mt-4 font-serif font-bold text-navy tracking-tight text-3xl md:text-4xl leading-tight">
            One job. One day. Real result.
          </h2>
          <p className="mt-4 text-lg text-navy-muted leading-relaxed">
            This is an actual C2H Energy install — not a stock photo. Drag to
            compare the rusted gas system that used to sit here with the heat
            pump that replaced it, same day.
          </p>
          <div className="my-7 border-t border-navy/10" />
          <div className="space-y-4">
            <p className="flex items-center gap-3 text-base font-medium text-navy">
              <Home className="w-5 h-5 text-gdark shrink-0" />
              Better for your home. Better for the planet.
            </p>
            <p className="flex items-center gap-3 text-base font-medium text-navy">
              <CalendarClock className="w-5 h-5 text-gdark shrink-0" />
              Ahead of Victoria's 2027 gas hot water phase-out
            </p>
          </div>
          <button
            onClick={scrollToForm}
            data-testid="real-results-link"
            className="mt-7 text-base font-semibold text-gdark underline decoration-gdark underline-offset-4 hover:decoration-2 transition-[text-decoration] duration-200"
          >
            Get the same result at your place →
          </button>
        </motion.div>

        <motion.figure
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.6, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
          data-testid="result-card-slider"
          className="relative rotate-[1.2deg]"
          style={{ boxShadow: "0 28px 60px rgba(1,27,71,0.28)", borderRadius: "20px" }}
        >
          <div
            ref={trackRef}
            data-testid="ba-slider"
            className="relative overflow-hidden rounded-[20px] aspect-[4/5] max-h-[540px] w-full cursor-ew-resize select-none touch-none"
            onPointerDown={(e) => {
              dragging.current = true;
              e.currentTarget.setPointerCapture(e.pointerId);
              setFromClientX(e.clientX);
            }}
            onPointerMove={(e) => dragging.current && setFromClientX(e.clientX)}
            onPointerUp={() => (dragging.current = false)}
            onPointerCancel={() => (dragging.current = false)}
          >
            <img
              src="/images/rinnai-after.webp"
              alt="New Rinnai heat pump hot water system installed by C2H Energy"
              data-testid="result-photo-after"
              className="absolute inset-0 w-full h-full object-cover"
              draggable="false"
            />
            <img
              src="/images/rinnai-before.webp"
              alt="Old gas hot water system before replacement"
              data-testid="result-photo-before"
              className="absolute inset-0 w-full h-full object-cover"
              style={{ clipPath: `inset(0 ${100 - pct}% 0 0)` }}
              draggable="false"
            />

            <span
              data-testid="result-chip-before"
              className="absolute top-3 left-3 px-3 py-1.5 text-xs font-bold uppercase tracking-[0.2em] bg-navy text-white rounded-md"
            >
              Before
            </span>
            <span
              data-testid="result-chip-now"
              className="absolute top-3 right-3 px-3 py-1.5 text-xs font-bold uppercase tracking-[0.2em] bg-gdark text-white rounded-full"
            >
              Now
            </span>

            <div
              aria-hidden="true"
              className="absolute top-0 bottom-0 w-0.5 bg-white"
              style={{ left: `${pct}%` }}
            >
              <span className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center">
                <ChevronsLeftRight className="w-5 h-5 text-navy" />
              </span>
            </div>

            <div
              className="absolute bottom-0 inset-x-0 p-5 pointer-events-none"
              style={{
                background:
                  "linear-gradient(to top, rgba(1,27,71,0.92) 0%, rgba(1,27,71,0.55) 55%, transparent 100%)",
              }}
            >
              <span className="inline-block bg-cream text-navy text-[11px] font-bold uppercase tracking-[0.2em] rounded-full px-3 py-1">
                Real C2H Rinnai install
              </span>
              <p
                data-testid="result-caption"
                className="mt-2 font-serif font-bold text-white text-xl md:text-2xl leading-snug"
              >
                South Morang – gas out, Rinnai in, done in one morning.
              </p>
              <div className="mt-3 flex gap-2">
                {[
                  ["1 day", "start to finish", CalendarClock],
                  ["Licensed", "& insured", BadgeCheck],
                  ["Rebates", "handled", ReceiptText],
                ].map(([big, small, Icon]) => (
                  <span
                    key={big}
                    className="bg-white/10 rounded-lg px-3 py-2 text-left"
                  >
                    <span className="flex items-center gap-1.5 text-sm font-bold text-white">
                      <Icon className="w-3.5 h-3.5 text-glight shrink-0" />
                      {big}
                    </span>
                    <span className="block text-[11px] text-[#c3ccd8]">
                      {small}
                    </span>
                  </span>
                ))}
              </div>
            </div>
          </div>
        </motion.figure>
      </div>
    </section>
  );
};

export default RealResults;
